/**
 * PATH: packages/job-hub/src/jobs/qa/qaClipboard.ts
 * FILE_ID: JOBHUB-QA-CLIPBOARD-001
 * PURPOSE: Session clipboard for a QA, area, checklist, or row set.
 * WHY: Copy survives a reload the same way a quote-builder node copy does.
 */
export type QaClipboardKind = 'qa' | 'area' | 'checklist' | 'rows' | 'level'

export interface QaClipboard {
  kind: QaClipboardKind
  ids: string[]
  label: string
  jobId: number
  inspectionId: string
}

const KEY = 'qa.clipboard'
const KINDS = new Set<QaClipboardKind>(['qa', 'area', 'checklist', 'rows', 'level'])

// Reused from: packages/quote-builder/src/lib/productTreeClipboard.ts (writeSession)
function writeSession(entry: QaClipboard | null): void {
  try {
    if (!entry) {
      sessionStorage.removeItem(KEY)
      return
    }
    sessionStorage.setItem(KEY, JSON.stringify(entry))
  } catch {
    /* private mode */
  }
}

// Reused from: packages/quote-builder/src/lib/productTreeClipboard.ts (readSession)
function readSession(): QaClipboard | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as QaClipboard
    if (!parsed || !KINDS.has(parsed.kind) || !Array.isArray(parsed.ids) || parsed.ids.length === 0) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function writeQaClipboard(entry: QaClipboard): void {
  writeSession(entry)
}

export function readQaClipboard(): QaClipboard | null {
  return readSession()
}

export function clearQaClipboard(): void {
  writeSession(null)
}

export function rowCopyIds(itemId: string, selectedIds: readonly string[]): string[] {
  return selectedIds.includes(itemId) ? [...selectedIds] : [itemId]
}

export function rowMenuClip(
  item: { id: string; title?: string; checklist_instance?: string },
  selectedIds: readonly string[],
  inspectionId: string,
) {
  const ids = rowCopyIds(item.id, selectedIds)
  const clip = readQaClipboard()
  return {
    copyCount: ids.length,
    pasteLabel: clip?.kind === 'rows' ? clip.label : null,
    sourceIds: clip?.kind === 'rows' ? clip.ids : [],
    onCopy: () => writeQaClipboard({
      kind: 'rows',
      ids,
      label: ids.length > 1 ? `${ids.length} rows` : (item.title || 'Row'),
      jobId: 0,
      inspectionId,
    }),
  }
}
